// Раздел «Конструктор процессов»: создание и правка процессов-из-базы (bpe_def).
//
// ЗАЧЕМ ЭТОТ ЭКРАН. Решение владельца 31.07.2026: «по процессам мне нужна система» —
// процессы задаются ДАННЫМИ и правятся из CRM, а не переписываются программистом.
// Контракт формата — ФОРМАТ_ПРОЦЕССОВ.md; этот экран умеет ровно то, что в контракте,
// и ничего сверх: шагов записи в Bitrix у формата нет намеренно.
//
// ДВА СОРТА ПРОЦЕССОВ, И ЭКРАН ИХ РАЗЛИЧАЕТ. Процессы из базы (source='db') правятся
// здесь. Прошитые Python-спеки (source='python', bp_specs_bitrix) — ТОЛЬКО ПРОСМОТР:
// править их значит править код, и врать редактируемой формой нельзя.
//
// ПЕРЕКЛЮЧАТЕЛЬ «ВКЛЮЧЁН» ГОВОРИТ ПРАВДУ ЦЕЛИКОМ: даже включённый процесс исполняет
// глобальный выключатель BPE_AUTO, а он сейчас выключен — задания лягут черновиками.
// Молчать об этом нельзя: человек включит процесс и будет ждать реальных заданий.
//
// ПРОВЕРКА ПЕРЕД СОХРАНЕНИЕМ — ДВАЖДЫ. Клиентская (api/processDefs.проверитьФорму)
// отвечает мгновенно и по месту; серверная (BFF) не верит браузеру. Ошибки сервера
// показываются словами, все разом — человек правит форму один раз.

import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import {
  ИМЕНА_КОМУ, ИМЕНА_СУЩНОСТЕЙ, ИМЕНА_ТРИГГЕРОВ, ИМЕНА_ШАГОВ, НетДоступа, ОшибкаФормы,
  СУЩНОСТИ, архивироватьПроцесс, проверитьФорму, сохранитьПроцесс, шаговУ,
  useПроцесс, useПроцессы,
} from '../api/processDefs'
import type {
  ВидТриггера, ВидШага, Кому, Процесс, Спека, ТелоСохранения, Триггер, Условие, Шаг,
} from '../api/processDefs'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtText, pluralN } from '../lib/format'

// Ряд элементов в строку — та же раскладка, что у `.filters` в styles.css, и тот же
// довод, что в BpeTasks.tsx: это поля формы, а не сужающие фильтры, FilterBar им не дом.
const РЯД = {
  display: 'flex', gap: 'var(--s3)', flexWrap: 'wrap', alignItems: 'flex-end',
} as const

const ПРЕДУПРЕЖДЕНИЕ_BPE_AUTO =
  'Реальную отправку держит глобальный выключатель BPE_AUTO, и сейчас он выключен: '
  + 'движок посчитает шаги, но задания лягут черновиками, уведомления не уйдут. '
  + 'Включение BPE_AUTO — отдельное решение владельца, не этот переключатель.'

/** Ярлык состояния процесса — тем же словарём бейджей, что и весь CRM. */
function видПроцесса(п: Процесс): { ярлык: string; класс: string } {
  if (п.archived_at) return { ярлык: 'архив', класс: 'badge lose' }
  if (п.source === 'python') return { ярлык: 'прошит в коде', класс: 'badge work' }
  return п.enabled
    ? { ярлык: 'включён', класс: 'badge won' }
    : { ярлык: 'выключен', класс: 'badge' }
}

const имяСущности = (е: string) => ИМЕНА_СУЩНОСТЕЙ[е] ?? е

// ───────────────────────────── экран ─────────────────────────────

export default function ProcessBuilder() {
  const список = useПроцессы()
  const [выбран, setВыбран] = useState<string | null>(null)
  const [новый, setНовый] = useState(false)
  const [сущность, setСущность] = useState('')

  const все = список.data?.rows ?? []
  const сущности = [...new Set(все.map((п) => п.entity))]
  const строки = сущность ? все.filter((п) => п.entity === сущность) : все

  return (
    <>
      <div className="page-head">
        <h1>Конструктор процессов</h1>
        <span className="spacer" />
        <span className="hint">
          {список.data
            ? pluralN(все.length, 'процесс', 'процесса', 'процессов')
            : ''}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 'var(--s4)', alignItems: 'flex-start',
                    flexWrap: 'wrap' }}>
        {/* ── слева: список ── */}
        <div className="card" style={{ flex: '1 1 300px', minWidth: 280, maxWidth: 420 }}>
          <div style={РЯД}>
            <div className="f" style={{ flex: 1 }}>
              <label htmlFor="pbent">Сущность</label>
              <select id="pbent" value={сущность}
                      onChange={(e) => setСущность(e.target.value)}>
                <option value="">любая</option>
                {сущности.map((е) => (
                  <option key={е} value={е}>{имяСущности(е)}</option>
                ))}
              </select>
            </div>
            <button type="button"
                    onClick={() => { setНовый(true); setВыбран(null) }}>
              + новый процесс
            </button>
          </div>

          <QueryState isLoading={список.isLoading} error={список.error}>
            <ul style={{ listStyle: 'none', margin: '12px 0 0', padding: 0 }}>
              {строки.map((п) => {
                const вид = видПроцесса(п)
                const шагов = шаговУ(п)
                return (
                  <li key={п.code} style={{ marginBottom: 8 }}>
                    <button type="button"
                            style={{ width: '100%', textAlign: 'left' }}
                            disabled={!новый && выбран === п.code}
                            onClick={() => { setНовый(false); setВыбран(п.code) }}>
                      <b>{fmtText(п.title)}</b>
                      <span className={вид.класс} style={{ marginLeft: 6 }}>
                        {вид.ярлык}
                      </span>
                      <div className="hint">
                        {п.code} · {имяСущности(п.entity)}
                        {шагов !== null
                          ? ' · ' + pluralN(шагов, 'шаг', 'шага', 'шагов') : ''}
                      </div>
                    </button>
                  </li>
                )
              })}
              {список.data && строки.length === 0 && (
                <li className="empty">
                  <div className="empty-title">Процессов пока нет</div>
                  <div className="empty-hint">
                    Нажмите «+ новый процесс» — он сохранится черновиком
                    (выключенным), пока вы его не включите.
                  </div>
                </li>
              )}
            </ul>
          </QueryState>
        </div>

        {/* ── справа: редактор или просмотр ── */}
        <div className="card" style={{ flex: '2 1 420px', minWidth: 360 }}>
          {новый ? (
            // После сохранения нового процесса редактор ОСТАЁТСЯ на экране со своим
            // сообщением, а не подменяется карточкой: смена формы под руками съедала
            // бы «Сохранено…», и человек не узнал бы, чем кончилось. Слева процесс
            // уже в списке (invalidateQueries) — открыть его карточку можно оттуда.
            <Редактор key="+новый" исходный={null}
                      сохранён={(код) => setВыбран(код)} />
          ) : выбран ? (
            <Карточка код={выбран} закрыт={() => setВыбран(null)} />
          ) : (
            <div className="empty">
              <div className="empty-title">Выберите процесс слева</div>
              <div className="empty-hint">
                Или заведите новый: он описывается данными — триггер, условия и шаги, —
                и правится здесь же, без программиста.
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

// ─────────────────────── карточка: загрузка и выбор формы ───────────────────────

function Карточка({ код, закрыт }: { код: string; закрыт: () => void }) {
  const зпр = useПроцесс(код)
  const п = зпр.data
  return (
    <QueryState isLoading={зпр.isLoading} error={зпр.error}>
      {п && (п.source === 'python'
        ? <Просмотр п={п} />
        // key: смена процесса обязана пересобрать локальное состояние формы с нуля,
        // иначе поля одного процесса перетекут в другой.
        : <Редактор key={п.code} исходный={п} сохранён={() => undefined}
                    архивирован={закрыт} />)}
    </QueryState>
  )
}

/** Прошитый Python-процесс: только просмотр, и экран говорит почему. */
function Просмотр({ п }: { п: Процесс }) {
  const вид = видПроцесса(п)
  return (
    <>
      <div className="page-head" style={{ marginBottom: 8 }}>
        <b>{fmtText(п.title)}</b>
        <span className={вид.класс} style={{ marginLeft: 8 }}>{вид.ярлык}</span>
      </div>
      <div className="notice" style={{ marginBottom: 12 }}>
        <b>Этот процесс прошит в коде — только просмотр.</b>
        <div style={{ marginTop: 6 }}>
          Он описан Python-спекой (bp_specs_bitrix), а не данными: править его из
          конструктора нельзя, редактируемая форма здесь врала бы. Правка — через
          изменение кода.
        </div>
      </div>
      <div className="hint">
        Код: {п.code} · сущность: {имяСущности(п.entity)}
      </div>
      {п.spec && (
        // НЕ .table-wrap: этот класс — про таблицы, которые на телефоне разбираются
        // на карточки (mobile-cards.test.ts), а здесь <pre>. Просто прокрутка вбок.
        <div style={{ marginTop: 12, overflowX: 'auto' }}>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(п.spec, null, 2)}
          </pre>
        </div>
      )}
    </>
  )
}

// ───────────────────────────── редактор ─────────────────────────────

function Редактор({ исходный, сохранён, архивирован }: {
  исходный: Процесс | null
  сохранён: (код: string) => void
  архивирован?: () => void
}) {
  const qc = useQueryClient()
  const спека: Спека = исходный?.spec
    ?? { триггер: { тип: 'создание' }, условия: [], шаги: [] }

  const [title, setTitle] = useState(исходный?.title ?? '')
  const [code, setCode] = useState(исходный?.code ?? '')
  const [entity, setEntity] = useState(исходный?.entity ?? 'deal')
  const [enabled, setEnabled] = useState(исходный?.enabled ?? false)
  const [триггер, setТриггер] = useState<Триггер>(спека.триггер ?? { тип: 'создание' })
  const [условия, setУсловия] = useState<Условие[]>(спека.условия ?? [])
  const [шаги, setШаги] = useState<Шаг[]>(спека.шаги ?? [])
  const [ошибки, setОшибки] = useState<string[]>([])
  // Код фиксируется после первого удачного сохранения: он — ключ идемпотентности
  // (bpe_fired), и «поправить код» дальше означало бы молча завести ВТОРОЙ процесс.
  const [кодЗакреплён, setКодЗакреплён] = useState(false)
  const [отказ, setОтказ] = useState('')
  const [занят, setЗанят] = useState(false)
  const [итог, setИтог] = useState('')

  const вАрхиве = !!исходный?.archived_at
  const итем = entity.startsWith('item:')

  /** Триггер без полей чужого типа: смена типа не должна тащить старые поля в базу. */
  function чистыйТриггер(): Триггер {
    const т: Триггер = { тип: триггер.тип }
    if (триггер.тип === 'стадия') т.стадия = (триггер.стадия ?? '').trim()
    if (триггер.тип === 'поле' || триггер.тип === 'срок') {
      т.поле = (триггер.поле ?? '').trim()
    }
    if (триггер.тип === 'поле') т.значение = (триггер.значение ?? '').trim()
    if (триггер.тип === 'срок') т.срок_дней = триггер.срок_дней
    return т
  }

  async function сохранить() {
    const тело: ТелоСохранения = {
      code: code.trim(), title: title.trim(), entity: entity.trim(), enabled,
      spec: { триггер: чистыйТриггер(), условия, шаги },
    }
    const ош = проверитьФорму(тело)
    setОшибки(ош)
    setОтказ('')
    setИтог('')
    if (ош.length) return
    setЗанят(true)
    try {
      await сохранитьПроцесс(тело)
      await qc.invalidateQueries({ queryKey: ['bpe-defs'] })
      setИтог(enabled
        ? 'Сохранено и включено. ' + ПРЕДУПРЕЖДЕНИЕ_BPE_AUTO
        : 'Сохранено выключенным (черновик): движок его не берёт.')
      setКодЗакреплён(true)
      сохранён(тело.code)
    } catch (e) {
      if (e instanceof ОшибкаФормы) setОшибки(e.ошибки)
      else if (e instanceof НетДоступа) setОтказ(String((e as Error).message))
      else setОтказ(e instanceof Error ? e.message : String(e))
    } finally {
      setЗанят(false)
    }
  }

  async function вАрхив() {
    // confirm, а не молча: архив снимает процесс с исполнения.
    if (!window.confirm(`Убрать процесс «${title || code}» в архив? `
      + 'Движок перестанет его брать; строка останется в базе.')) return
    setЗанят(true)
    setОтказ('')
    try {
      await архивироватьПроцесс(code)
      await qc.invalidateQueries({ queryKey: ['bpe-defs'] })
      архивирован?.()
    } catch (e) {
      setОтказ(e instanceof Error ? e.message : String(e))
    } finally {
      setЗанят(false)
    }
  }

  return (
    <>
      <div className="page-head" style={{ marginBottom: 8 }}>
        <b>{исходный ? 'Процесс' : 'Новый процесс'}</b>
        {вАрхиве && <span className="badge lose" style={{ marginLeft: 8 }}>архив</span>}
      </div>

      {/* ── шапка: название, код, сущность ── */}
      <div style={РЯД}>
        <div className="f" style={{ flex: 2, minWidth: 220 }}>
          <label htmlFor="pbt">Название</label>
          <input id="pbt" value={title} onChange={(e) => setTitle(e.target.value)}
                 placeholder="например: Пролонгация договора" />
        </div>
        <div className="f" style={{ minWidth: 180 }}>
          <label htmlFor="pbc">Код (латиницей, стабильный)</label>
          {/* Код существующего процесса не правится: он — ключ идемпотентности
              (bpe_fired) и имя в логах. Сменить код = завести другой процесс. */}
          <input id="pbc" value={code} disabled={!!исходный || кодЗакреплён}
                 onChange={(e) => setCode(e.target.value)}
                 placeholder="contract-prolong" />
        </div>
        <div className="f">
          <label htmlFor="pbe">Сущность</label>
          <select id="pbe" value={итем ? 'item' : entity}
                  onChange={(e) => setEntity(
                    e.target.value === 'item' ? 'item:' : e.target.value)}>
            {СУЩНОСТИ.map((е) => (
              <option key={е} value={е}>{имяСущности(е)}</option>
            ))}
            <option value="item">смарт-процесс (item:…)</option>
          </select>
        </div>
        {итем && (
          <div className="f" style={{ minWidth: 140 }}>
            <label htmlFor="pbei">Номер типа (entityTypeId)</label>
            <input id="pbei" value={entity.slice('item:'.length)}
                   onChange={(e) => setEntity('item:' + e.target.value.trim())}
                   placeholder="например: 134" />
          </div>
        )}
      </div>

      <Блок заголовок="Триггер — когда процесс запускается">
        <div style={РЯД}>
          <div className="f">
            <label htmlFor="pbtr">Тип</label>
            <select id="pbtr" value={триггер.тип}
                    onChange={(e) => setТриггер({ тип: e.target.value as ВидТриггера })}>
              {(Object.keys(ИМЕНА_ТРИГГЕРОВ) as ВидТриггера[]).map((т) => (
                <option key={т} value={т}>{ИМЕНА_ТРИГГЕРОВ[т]}</option>
              ))}
            </select>
          </div>
          {триггер.тип === 'стадия' && (
            <div className="f" style={{ minWidth: 220 }}>
              <label htmlFor="pbtrs">Код стадии</label>
              <input id="pbtrs" value={триггер.стадия ?? ''}
                     onChange={(e) => setТриггер({ ...триггер, стадия: e.target.value })}
                     placeholder="например: WON" />
            </div>
          )}
          {(триггер.тип === 'поле' || триггер.тип === 'срок') && (
            <div className="f" style={{ minWidth: 220 }}>
              <label htmlFor="pbtrf">Код поля</label>
              <input id="pbtrf" value={триггер.поле ?? ''}
                     onChange={(e) => setТриггер({ ...триггер, поле: e.target.value })}
                     placeholder="например: UF_CRM_DATE_END" />
            </div>
          )}
          {триггер.тип === 'поле' && (
            <div className="f" style={{ minWidth: 180 }}>
              <label htmlFor="pbtrv">Ожидаемое значение</label>
              <input id="pbtrv" value={триггер.значение ?? ''}
                     onChange={(e) => setТриггер({ ...триггер, значение: e.target.value })} />
            </div>
          )}
          {триггер.тип === 'срок' && (
            <div className="f" style={{ minWidth: 140 }}>
              <label htmlFor="pbtrd">За сколько дней</label>
              <input id="pbtrd" type="number" min={1} value={триггер.срок_дней ?? ''}
                     onChange={(e) => setТриггер({
                       ...триггер,
                       срок_дней: e.target.value === '' ? '' : Number(e.target.value),
                     })} />
            </div>
          )}
        </div>
      </Блок>

      <Блок заголовок="Условия — все должны выполниться"
            подсказка="Пустой список = процесс запускается по одному триггеру.">
        {условия.map((у, i) => (
          <div style={{ ...РЯД, marginBottom: 8 }} key={i}>
            <div className="f" style={{ minWidth: 200 }}>
              <label htmlFor={`pbuf${i}`}>Поле</label>
              <input id={`pbuf${i}`} value={у.поле}
                     onChange={(e) => setУсловия(
                       условия.map((x, k) => (k === i ? { ...x, поле: e.target.value } : x)))} />
            </div>
            <div className="f">
              <label htmlFor={`pbuo${i}`}>Операция</label>
              <select id={`pbuo${i}`} value={у.оп}
                      onChange={(e) => setУсловия(условия.map(
                        (x, k) => (k === i ? { ...x, оп: e.target.value as Условие['оп'] } : x)))}>
                {(['=', '!=', 'пусто', 'непусто', '>', '<'] as const).map((о) => (
                  <option key={о} value={о}>{о}</option>
                ))}
              </select>
            </div>
            {у.оп !== 'пусто' && у.оп !== 'непусто' && (
              <div className="f" style={{ minWidth: 160 }}>
                <label htmlFor={`pbuv${i}`}>Значение</label>
                <input id={`pbuv${i}`} value={у.значение ?? ''}
                       onChange={(e) => setУсловия(условия.map(
                         (x, k) => (k === i ? { ...x, значение: e.target.value } : x)))} />
              </div>
            )}
            <button type="button" title="убрать условие"
                    onClick={() => setУсловия(условия.filter((_, k) => k !== i))}>
              убрать
            </button>
          </div>
        ))}
        <button type="button"
                onClick={() => setУсловия([...условия, { поле: '', оп: '=', значение: '' }])}>
          + условие
        </button>
      </Блок>

      <Блок заголовок="Шаги — что движок сделает"
            подсказка={'«Задание» встаёт в раздел «Задания движка», «уведомление» — в '
              + 'очередь уведомлений (Telegram), «комментарий» — в ленту карточки своей '
              + 'схемы. Записи в Bitrix у шагов нет — намеренно. В тексте работают '
              + '{подстановки полей}.'}>
        {шаги.map((ш, i) => (
          <ШагКарточка key={i} ш={ш} номер={i + 1}
                       первый={i === 0} последний={i === шаги.length - 1}
                       вверх={() => setШаги(переставить(шаги, i, i - 1))}
                       вниз={() => setШаги(переставить(шаги, i, i + 1))}
                       убрать={() => setШаги(шаги.filter((_, k) => k !== i))}
                       правка={(нов) => setШаги(шаги.map((x, k) => (k === i ? нов : x)))} />
        ))}
        <div style={РЯД}>
          {(Object.keys(ИМЕНА_ШАГОВ) as ВидШага[]).map((т) => (
            <button key={т} type="button" onClick={() => setШаги([...шаги, пустойШаг(т)])}>
              + {ИМЕНА_ШАГОВ[т].toLowerCase()}
            </button>
          ))}
        </div>
      </Блок>

      {/* ── включён + правда про BPE_AUTO ── */}
      <div style={{ marginTop: 16 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={enabled} style={{ width: 'auto' }}
                 onChange={(e) => setEnabled(e.target.checked)} />
          <b>Процесс включён</b> (выключен = черновик, движок его не берёт)
        </label>
        {enabled && (
          <div className="notice" style={{ marginTop: 8 }}>
            {ПРЕДУПРЕЖДЕНИЕ_BPE_AUTO}
          </div>
        )}
      </div>

      {/* ── ошибки: причина ВЫШЕ кнопки, как в BpeTasks ── */}
      {ошибки.length > 0 && (
        <div className="notice" style={{ marginTop: 12 }}>
          <b>Пока не сохраняется — форма заполнена не до конца:</b>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {ошибки.map((о) => <li key={о}>{о}</li>)}
          </ul>
        </div>
      )}
      {отказ && (
        <div className="notice" style={{ marginTop: 12 }}>
          <b>Сервер не принял:</b>
          <div style={{ marginTop: 6 }}>{отказ}</div>
        </div>
      )}
      {итог && !ошибки.length && !отказ && (
        <div className="notice" style={{ marginTop: 12 }}>{итог}</div>
      )}

      <div style={{ ...РЯД, marginTop: 12 }}>
        <button type="button" disabled={занят} onClick={сохранить}>
          {занят ? 'Сохраняем…' : 'Сохранить'}
        </button>
        {исходный && !вАрхиве && (
          <button type="button" disabled={занят} onClick={вАрхив}>
            В архив
          </button>
        )}
        {исходный?.updated_at && (
          <span className="hint">
            правился {fmtDate(исходный.updated_at, true)}
            {исходный.updated_by ? ` (${исходный.updated_by})` : ''}
          </span>
        )}
      </div>
    </>
  )
}

// ───────────────────────────── кирпичики ─────────────────────────────

function Блок({ заголовок, подсказка, children }: {
  заголовок: string
  подсказка?: string
  children: React.ReactNode
}) {
  return (
    <div style={{ marginTop: 16 }}>
      <b>{заголовок}</b>
      {подсказка && <div className="hint" style={{ margin: '4px 0 8px' }}>{подсказка}</div>}
      <div style={{ marginTop: подсказка ? 0 : 8 }}>{children}</div>
    </div>
  )
}

function пустойШаг(т: ВидШага): Шаг {
  if (т === 'комментарий') return { тип: т, текст: '' }
  if (т === 'уведомление') {
    return { тип: т, канал: 'tg', кому: { вид: 'роль', значение: '' }, текст: '' }
  }
  return { тип: т, кому: { вид: 'роль', значение: '' }, текст: '', срок_дней: '' }
}

function переставить(шаги: Шаг[], из: number, в: number): Шаг[] {
  if (в < 0 || в >= шаги.length) return шаги
  const нов = [...шаги]
  const [ш] = нов.splice(из, 1)
  нов.splice(в, 0, ш)
  return нов
}

function ШагКарточка({ ш, номер, первый, последний, вверх, вниз, убрать, правка }: {
  ш: Шаг
  номер: number
  первый: boolean
  последний: boolean
  вверх: () => void
  вниз: () => void
  убрать: () => void
  правка: (ш: Шаг) => void
}) {
  const сКому = ш.тип !== 'комментарий'
  return (
    <div className="card" style={{ marginBottom: 8 }}>
      <div style={{ ...РЯД, alignItems: 'center', marginBottom: 8 }}>
        <b>Шаг {номер}: {ИМЕНА_ШАГОВ[ш.тип]}</b>
        <span className="spacer" />
        <button type="button" disabled={первый} onClick={вверх}
                title="переставить выше">↑</button>
        <button type="button" disabled={последний} onClick={вниз}
                title="переставить ниже">↓</button>
        <button type="button" onClick={убрать}>убрать</button>
      </div>
      <div style={РЯД}>
        {сКому && (
          <>
            <div className="f">
              <label htmlFor={`pbsk${номер}`}>Кому (вид)</label>
              <select id={`pbsk${номер}`} value={ш.кому?.вид ?? 'роль'}
                      onChange={(e) => правка({
                        ...ш,
                        кому: { вид: e.target.value as Кому['вид'],
                                значение: ш.кому?.значение ?? '' },
                      })}>
                {(Object.keys(ИМЕНА_КОМУ) as Кому['вид'][]).map((в) => (
                  <option key={в} value={в}>{ИМЕНА_КОМУ[в]}</option>
                ))}
              </select>
            </div>
            <div className="f" style={{ minWidth: 180 }}>
              <label htmlFor={`pbsz${номер}`}>Кому (значение)</label>
              <input id={`pbsz${номер}`} value={ш.кому?.значение ?? ''}
                     onChange={(e) => правка({
                       ...ш,
                       кому: { вид: ш.кому?.вид ?? 'роль', значение: e.target.value },
                     })}
                     placeholder={ш.кому?.вид === 'bitrix_id' ? 'например: 10656'
                       : ш.кому?.вид === 'поле_записи' ? 'например: ASSIGNED_BY_ID'
                         : 'например: юрист'} />
            </div>
          </>
        )}
        {ш.тип === 'уведомление' && (
          <div className="f">
            <label htmlFor={`pbsc${номер}`}>Канал</label>
            <select id={`pbsc${номер}`} value={ш.канал ?? 'tg'}
                    onChange={(e) => правка({ ...ш, канал: e.target.value as 'tg' })}>
              <option value="tg">Telegram</option>
            </select>
          </div>
        )}
        {ш.тип === 'задание' && (
          <div className="f" style={{ minWidth: 120 }}>
            <label htmlFor={`pbsd${номер}`}>Срок, дней</label>
            <input id={`pbsd${номер}`} type="number" min={1}
                   value={ш.срок_дней ?? ''}
                   onChange={(e) => правка({
                     ...ш,
                     срок_дней: e.target.value === '' ? '' : Number(e.target.value),
                   })}
                   placeholder="можно пусто" />
          </div>
        )}
        <div className="f" style={{ flex: 2, minWidth: 240 }}>
          <label htmlFor={`pbst${номер}`}>Текст</label>
          <input id={`pbst${номер}`} value={ш.текст}
                 onChange={(e) => правка({ ...ш, текст: e.target.value })}
                 placeholder="шаблон с {подстановками полей}" />
        </div>
      </div>
    </div>
  )
}
